import React, { useState } from 'react';
import { Box, Link, styled, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { PATH } from '../constants/routes';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { setAuth } from '../store/slices/authSlice';
import api from '../services/api';
import { setAccessToken } from '../services/authStorage';
import Button from '../components/Button';
import DrivioLogo from '../components/DrivioLogo';
import InputField from '../components/InputField';
import { getThemeColors } from '../helpers/themeStyles';

const LEFT_PANEL_IMAGE_URL = `${process.env.REACT_APP_URL}/assets/login.webp`;

const signInSchema = yup.object({
  email: yup
    .string()
    .trim()
    .required("L'adresse e-mail est requise.")
    .email('Entrez une adresse e-mail valide.'),
  password: yup.string().required('Le mot de passe est requis.'),
});

const SignInPage = styled(Box)(({ theme }) => ({
  height: '100vh',
  backgroundColor: theme.palette.background.default,
  padding: 40,
  boxSizing: 'border-box',
}));

const SignInLayout = styled(Box)(({ theme }) => {
  const colors = getThemeColors(theme);

  return {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'stretch',
    gap: 40,
    backgroundColor: colors.authLayoutBg,
    boxSizing: 'border-box',
  };
});

const LeftVisualPanel = styled(Box)({
  flex: 1.2,
  height: '100%',
  borderRadius: 20,
  overflow: 'hidden',
});

const LeftVisualImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
});

const RightAuthPanel = styled(Box)(({ theme }) => ({
  flex: 0.95,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  borderRadius: 20,
  backgroundColor: getThemeColors(theme).authPanelBg,
  padding: '80px 40px',
  boxSizing: 'border-box',
}));

const RightHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
});

const RightMain = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const FormContainer = styled(Box)({
  width: '100%',
  maxWidth: 400,
});

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: 40,
  fontSize: 40,
  fontWeight: 700,
  color: getThemeColors(theme).textPrimary,
  textAlign: 'center',
  letterSpacing: 0.06,
}));

const SignInForm = styled('form')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const ForgotPasswordLink = styled(Link)(({ theme }) => {
  const colors = getThemeColors(theme);

  return {
    marginTop: 4,
    fontSize: 14,
    fontWeight: 400,
    color: colors.textPrimary,
    textAlign: 'center',
    textDecoration: 'underline',
    cursor: 'pointer',
    '&:hover': {
      color: colors.textSecondary,
    },
  };
});

const hiddenAutofillTrapStyles = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: 'none',
};

const SignIn = () => {
  const [allowCredentialAutofill, setAllowCredentialAutofill] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(signInSchema),
    defaultValues: { email: '', password: '' },
  });

  const enableCredentialAutofill = () => {
    if (!allowCredentialAutofill) {
      setAllowCredentialAutofill(true);
    }
  };

  const onSubmit = async (data) => {
    try {
      const response = await api.post('/auth/sign-in', {
        email: data.email,
        password: data.password,
      });

      const { token, user } = response.data?.data || {};

      if (!token) return;

      if (token) setAccessToken(token);

      dispatch(setAuth(user));
      navigate(PATH.HOME);
    } catch {
      // Request errors are already normalized/toasted in api interceptor.
      // Swallow here to avoid unhandled promise rejections in the UI flow.
    }
  };

  return (
    <SignInPage>
      <SignInLayout>
        <LeftVisualPanel
          sx={{
            '@media (max-width: 1024px)': {
              display: 'none',
            },
          }}
        >
          <LeftVisualImage src={LEFT_PANEL_IMAGE_URL} alt="Visuel de connexion" />
        </LeftVisualPanel>

        <RightAuthPanel
          sx={{
            '@media (max-width: 1024px)': {
              flex: 1,
            },
          }}
        >
          <RightHeader>
            <DrivioLogo sx={{ fontSize: 24 }} />
          </RightHeader>

          <RightMain>
            <FormContainer>
              <Title>SE CONNECTER</Title>

              <SignInForm onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate>
                <input
                  type="text"
                  name="fake-username"
                  autoComplete="username"
                  tabIndex={-1}
                  aria-hidden="true"
                  style={hiddenAutofillTrapStyles}
                />
                <input
                  type="password"
                  name="fake-password"
                  autoComplete="current-password"
                  tabIndex={-1}
                  aria-hidden="true"
                  style={hiddenAutofillTrapStyles}
                />

                <Controller
                  name="email"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputField
                      fullWidth
                      type="email"
                      label="E-mail"
                      name="signin-email"
                      autoComplete={allowCredentialAutofill ? 'username' : 'off'}
                      InputProps={{ readOnly: !allowCredentialAutofill }}
                      onMouseDown={enableCredentialAutofill}
                      onFocus={enableCredentialAutofill}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      inputRef={field.ref}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      onClear={() => {
                        setValue('email', '', {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                      }}
                    />
                  )}
                />

                <Controller
                  name="password"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputField
                      fullWidth
                      type="password"
                      withPasswordToggle
                      label="Mot de passe"
                      name="signin-password"
                      autoComplete={allowCredentialAutofill ? 'current-password' : 'off'}
                      InputProps={{ readOnly: !allowCredentialAutofill }}
                      onMouseDown={enableCredentialAutofill}
                      onFocus={enableCredentialAutofill}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      inputRef={field.ref}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      onClear={() => {
                        setValue('password', '', {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                      }}
                    />
                  )}
                />

                <ForgotPasswordLink component={RouterLink} to={PATH.FORGOT_PASSWORD}>
                  Mot de passe oublié ?
                </ForgotPasswordLink>

                <Button
                  fullWidth
                  disableElevation
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={(theme) => {
                    const colors = getThemeColors(theme);

                    return {
                      marginTop: 2,
                      backgroundColor: colors.accent,
                      color: colors.activeTextOnAccent,
                      '&:hover': {
                        backgroundColor: colors.accentHover,
                      },
                    };
                  }}
                >
                  SE CONNECTER
                </Button>
              </SignInForm>
            </FormContainer>
          </RightMain>
        </RightAuthPanel>
      </SignInLayout>
    </SignInPage>
  );
};

export default SignIn;
